import { useState } from 'react'
import { Link, useFetcher } from 'react-router'
import { uploadsUrl } from '@trinity/utils'
import {
  Box,
  Button,
  Close,
  Dialog,
  IconButton,
  KeyboardArrowLeft,
  Stack,
  TextField,
  Typography,
} from '../../components'
import { ImageNotFound } from '../../assets'

export type ClientImagesProps = {
  client: TrinityAPI.ClientExtendedType
  images: TrinityAPI.UploadedImageType[]
}

type ClientImagesFetcherData = { intent?: string; images: TrinityAPI.UploadedImageType[] }

/** Customer images management UI; route modules supply loader + clientAction and render this with loaderData. */
export function ClientImages({ client, images }: ClientImagesProps) {
  const fetcher = useFetcher<ClientImagesFetcherData>()
  const displayedImages = images

  const [imageToDelete, setImageToDelete] = useState<number | null>(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [pendingFile, setPendingFile] = useState<File | null>(null)

  const closeUploadDialog = () => {
    setUploadOpen(false)
    setPendingFile(null)
  }

  const handleFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    setPendingFile(file ?? null)
  }

  const handleCloseDeleteDialog = () => {
    setImageToDelete(null)
  }

  return (
    <Stack p={{ mobile: 2, tablet: 4 }} spacing={3}>
      <Stack direction='row' alignItems='center' spacing={1}>
        <IconButton component={Link} to='..' relative='path'>
          <KeyboardArrowLeft />
        </IconButton>
        <Typography variant='body3' color='text.secondary'>
          {client.name} / Images
        </Typography>
      </Stack>

      <Typography variant='h1'>Customer Images</Typography>

      <Typography variant='body3' color='text.secondary'>
        These images are attached to this customer profile.
      </Typography>

      <Button variant='outlined' onClick={() => setUploadOpen(true)} size='small' sx={{ alignSelf: 'flex-start' }}>
        Add Image
      </Button>

      <DisplayedImages images={displayedImages} setImageToDelete={setImageToDelete} />

      <Dialog open={uploadOpen} onClose={closeUploadDialog}>
        <fetcher.Form method='post' encType='multipart/form-data' onSubmit={closeUploadDialog}>
          <Stack spacing={2} p={2} sx={{ minWidth: { mobile: '100%', tablet: 360 } }}>
            <Typography variant='h6'>Upload image</Typography>
            <input type='hidden' name='intent' value='saveImage' />
            <Button component='label' variant='outlined' size='small' sx={{ alignSelf: 'flex-start' }}>
              Choose image
              <input type='file' name='image' hidden accept='image/*' onChange={handleFileChosen} />
            </Button>
            {pendingFile && (
              <img src={URL.createObjectURL(pendingFile)} alt={pendingFile.name} style={{ maxWidth: '100%' }} />
            )}
            <TextField name='description' label='Description' size='small' fullWidth multiline minRows={2} />
            <Stack direction='row' spacing={2} justifyContent='flex-end'>
              <Button type='button' variant='outlined' onClick={closeUploadDialog}>
                Cancel
              </Button>
              <Button type='submit' variant='contained' disabled={!pendingFile}>
                Upload
              </Button>
            </Stack>
          </Stack>
        </fetcher.Form>
      </Dialog>

      <Dialog open={imageToDelete !== null} onClose={handleCloseDeleteDialog}>
        <fetcher.Form method='post' onSubmit={() => setImageToDelete(null)}>
          <input type='hidden' name='intent' value='deleteImage' />
          <input type='hidden' name='imageId' value={String(imageToDelete ?? '')} />
          <Stack spacing={6} p={2}>
            <Typography variant='h6'>Delete image?</Typography>
            <Typography>Are you sure you want to delete this image?</Typography>
            <Stack direction='row' spacing={2} justifyContent='flex-end'>
              <Button variant='outlined' onClick={handleCloseDeleteDialog}>
                Cancel
              </Button>
              <Button type='submit' color='error'>
                Delete
              </Button>
            </Stack>
          </Stack>
        </fetcher.Form>
      </Dialog>
    </Stack>
  )
}

function DisplayedImages({
  images,
  setImageToDelete,
}: {
  images: TrinityAPI.UploadedImageType[]
  setImageToDelete: (id: number) => void
}) {
  if (images.length === 0) {
    return (
      <Typography variant='body3' color='text.secondary' align='center' py={4}>
        No images yet. Add an image to have it appear here and in order garment details.
      </Typography>
    )
  }

  return (
    <Stack direction='row' flexWrap='wrap' gap={1.5} sx={{ justifyContent: 'flex-start' }}>
      {images.map((image, index) => (
        <Box key={image.id} sx={{ position: 'relative', width: { mobile: 'calc(50% - 6px)', tablet: 200 } }}>
          <Box
            component='img'
            src={uploadsUrl(image.url)}
            alt={image.description ?? `Customer image ${index + 1}`}
            title={image.description ?? ''}
            onError={e => (e.currentTarget.src = ImageNotFound)}
            sx={{
              width: 1,
              borderRadius: theme => theme.shape.borderRadius,
              border: 'n100',
            }}
          />
          <IconButton
            size='small'
            aria-label='Delete image'
            onClick={() => setImageToDelete(image.id)}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              color: 'common.white',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.8)' },
            }}
          >
            <Close fontSize='small' />
          </IconButton>
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ display: 'block', wordBreak: 'break-word', mt: 0.5 }}
          >
            {image.description}
          </Typography>
        </Box>
      ))}
    </Stack>
  )
}
